import { useCallback, useEffect, useState } from 'react';
import { ReactiveJsonRoot } from '@ea-lab/reactive-json';
import { buildDataOverride } from '../core/urlDataOverride';
import AnnotationEditor from './AnnotationEditor';

const CAPTURE_UPLOAD_URL = '/decoupled-api/file/upload/node/task/field_attached_files';

/**
 * Nom UNIQUE à chaque envoi. Drupal numérote les collisions (capture.png,
 * capture_1.png…) et RÉUTILISE un nom libéré par une suppression : le navigateur
 * resservait alors sa copie en cache, donc une capture d'un signalement
 * précédent. Un nom horodaté supprime la classe entière de ce problème.
 */
const horodatage = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Téléverse un fichier dans les pièces jointes de tâche et rend son identifiant.
 *
 * Le fichier est temporaire tant que la tâche n'est pas enregistrée : si la
 * personne referme sans créer, Drupal le collecte de lui-même (les fichiers
 * temporaires non référencés expirent au cron).
 *
 * Un échec n'interrompt rien : un signalement sans sa capture, ou sans son
 * historique, reste un signalement.
 */
async function televerser(blob, nom) {
  try {
    const res = await fetch(CAPTURE_UPLOAD_URL, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Disposition': `file; filename="${nom}"`,
      },
      body: blob,
    }).then((r) => (r.ok ? r.json() : null));
    // Réponse = Syncable Object : `fid` et `url` sont sous `data`, pas à la
    // racine (seul `delete_url` est au premier niveau).
    if (res?.data?.fid) return { fid: String(res.data.fid), url: res.data.url || '' };
  }
  catch { /* voir le docblock */ }
  return { fid: '', url: '' };
}

/**
 * Le formulaire de signalement, en popin, SUR la page où l'on se trouve.
 *
 * Pourquoi ce composant existe (et pas un YAML) : rien en reactive-json ne sait
 * se monter sur TOUTES les routes indépendamment du YAML de chaque page, lire
 * `location.href`, ni recevoir un événement DOM émis par un script hors React.
 * Ces trois choses-là justifient du JSX — et rien de plus : l'écran affiché est
 * `/pages/report.yaml`, le même que la route `/report`, monté tel quel.
 *
 * Le contexte technique (URL, navigateur, erreurs JS) n'est pas recalculé ici :
 * il vient du widget `public/widget/report.js`, qui le collecte depuis le
 * chargement de la page et le passe dans l'événement. Un seul collecteur, qu'on
 * soit dans Orbit ou sur un site externe.
 *
 * DEUX ÉTAPES, et l'ordre est contraint : on annote AVANT d'ouvrir le
 * formulaire. Celui-ci est un rjBuild qui reçoit l'identifiant du fichier à son
 * montage ; annoter ensuite imposerait de le remonter avec un nouvel
 * identifiant, ce qui effacerait la saisie en cours. Une seule capture, un seul
 * téléversement, aucune remise à zéro.
 */
export const REPORT_OPEN_EVENT = 'orbit-report:open';

export default function ReportOverlay({ plugins }) {
  // `annotation` : { detail, blob } tant que l'éditeur est à l'écran.
  // `form`       : { content, dataOverride } une fois le formulaire prêt.
  const [annotation, setAnnotation] = useState(null);
  const [form, setForm] = useState(null);

  const close = useCallback(() => { setAnnotation(null); setForm(null); }, []);

  // Le widget vanilla (`public/widget/report.js`) regarde `window.OrbitCapture`
  // AVANT d'aller chercher `/widget/capture.js`. Sur une instance Orbit qui
  // pousse ses tickets vers une AUTRE instance — le défaut livré : la production
  // — il prend le chemin « site externe » et charge donc ce fichier depuis sa
  // PROPRE origine. Or c'est un artefact de build (`dist/widget/`) : sur le
  // serveur de développement, Vite répond l'index.html de la SPA en 200. Le
  // script « charge » sans rien définir, la capture est nulle, et le signalement
  // part sans image ni éditeur d'annotation — sans la moindre erreur nulle part.
  // Publier ici le module de l'application donne au widget la capture qu'il
  // cherche, en développement comme en production, sans rien charger.
  useEffect(() => {
    const notre = { capture: () => import('../../widget/capture.js').then((m) => m.capture()) };
    window.OrbitCapture = notre;
    return () => { if (window.OrbitCapture === notre) delete window.OrbitCapture; };
  }, []);

  // Téléverse la capture et l'historique (le cas échéant) puis monte le rjBuild.
  const ouvrirFormulaire = useCallback(async (detail, blob) => {
    // Les fichiers sont des Blobs : ils ne peuvent pas traverser le rjBuild, qui
    // ne transporte que des valeurs sérialisables. On les téléverse donc ici, et
    // seuls leurs IDENTIFIANTS (des scalaires) descendent dans le YAML.
    const capture = blob instanceof Blob
      ? await televerser(blob, `capture-${horodatage()}.png`)
      : { fid: '', url: '' };

    // Historique de conversation, quand le signalement vient de l'assistant du
    // builder. Markdown : la fiche le rend avec le même composant et le même
    // dialecte que le chat, donc il se relit tel qu'il s'est écrit.
    const transcript = detail.transcript?.blob instanceof Blob
      ? await televerser(detail.transcript.blob, detail.transcript.name || `conversation-${horodatage()}.md`)
      : { fid: '', url: '' };

    const search = new URLSearchParams({
      // Dit au rjBuild qu'il est monté en popin : il masque alors le chrome
      // d'Orbit, déjà présent derrière.
      embedded: '1',
      host: detail.host || window.location.hostname,
      pageUrl: detail.pageUrl || window.location.href,
      context: detail.context || '',
      captureFid: capture.fid,
      captureUrl: capture.url,
      transcriptFid: transcript.fid,
      // Description pré-rédigée (assistant du builder). Ici il n'y a pas d'URL
      // réelle — `buildDataOverride` analyse cette chaîne en mémoire — donc sa
      // longueur n'est pas un sujet.
      description: detail.description || '',
    }).toString();

    try {
      const content = await fetch('/pages/report.yaml').then((r) => (r.ok ? r.text() : null));
      if (!content) return;
      setAnnotation(null);
      setForm({ content, dataOverride: buildDataOverride(content, `?${search}`) });
    }
    catch { /* rjBuild injoignable : on n'ouvre pas une popin vide */ }
  }, []);

  useEffect(() => {
    const onOpen = async (event) => {
      const detail = (event && event.detail) || {};

      // La capture se fait ICI, pas dans le widget : `widget/capture.js` est un
      // artefact de build (dist/widget/), absent du serveur de développement —
      // l'y charger donne un 404 et un ticket sans image. Importé dynamiquement,
      // le MÊME module est découpé par Vite en morceau chargé à la demande, et
      // fonctionne en développement comme en production.
      let blob = detail.capture instanceof Blob ? detail.capture : null;
      if (!blob) {
        try {
          const mod = await import('../../widget/capture.js');
          blob = await mod.capture();
        }
        catch { /* pas de capture : on signale quand même */ }
      }

      if (blob) setAnnotation({ detail, blob });
      else await ouvrirFormulaire(detail, null);
    };

    const armed = (event) => {
      onOpen(event).finally(() => {
        // Le widget a mis son bouton en attente le temps de la capture : on le
        // libère dès que l'écran est prêt, succès ou non.
        window.dispatchEvent(new CustomEvent('orbit-report:ready'));
      });
    };
    window.addEventListener(REPORT_OPEN_EVENT, armed);
    return () => window.removeEventListener(REPORT_OPEN_EVENT, armed);
  }, [ouvrirFormulaire]);

  // Échap ferme, comme toute boîte de dialogue. L'éditeur gère sa propre touche
  // Échap (« sans annoter »), donc on ne l'écoute qu'à l'étape formulaire.
  useEffect(() => {
    if (!form) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [form, close]);

  if (!annotation && !form) return null;

  return (
    <div
      // Étagement : `.modal-scrim-over-chrome` (z-55, recette du DS). Le z-50
      // des modales d'Orbit ne suffit pas — le panneau Assistant l'occupe
      // aussi, et le signalement s'ouvrait DERRIÈRE la conversation. NE PAS
      // monter plus haut pour autant : les listes déroulantes
      // (FuzzySelectField) sont portées dans <body> en z-60 pour passer
      // au-dessus de cette couche ; les dépasser ferait disparaître le
      // sélecteur de projet derrière sa propre popin.
      className="modal-scrim-over-chrome fixed inset-0 flex items-start justify-center overflow-y-auto bg-slate-500/30 p-4 backdrop-blur-sm sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label="Signaler un problème"
      onClick={(e) => { if (e.target === e.currentTarget && form) close(); }}
    >
      <div className="relative w-full max-w-5xl rounded-lg bg-white shadow-2xl">
        <button
          type="button"
          onClick={close}
          aria-label="Fermer le signalement"
          className="absolute right-3 top-3 z-10 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          ✕
        </button>

        {annotation ? (
          <div className="max-h-[85vh] p-6">
            <AnnotationEditor
              blob={annotation.blob}
              onDone={(b) => ouvrirFormulaire(annotation.detail, b)}
              onDiscard={() => ouvrirFormulaire(annotation.detail, null)}
            />
          </div>
        ) : (
          <ReactiveJsonRoot
            maybeRawAppRjBuild={form.content}
            dataOverride={form.dataOverride}
            rjBuildFetchMethod="GET"
            plugins={plugins}
          />
        )}
      </div>
    </div>
  );
}
